import { useIntl } from 'react-intl';
import { IconTooltip, LoadingSkeleton, useQueryFetch } from 'sonata-react-components';
import { Formatters } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';

import { type DeviatedLineItemsCountRequestBody, getDeviatedLineItemsCount } from '@/services/lineItem.service';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';

import './deviatedLineItemsStats.scss';

type Props = {
  cacheSeed: string;
  filters: DeviatedLineItemsCountRequestBody;
};

const renderPercentage = (value: number | null | undefined) =>
  value == null ? '-' : Formatters.percentage(value, { base: 100, fixedDecimals: 2 });

const DeviatedLineItemsStats = ({ cacheSeed, filters }: Readonly<Props>) => {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const { id: userId = '' } = user.entity;

  // Same query key/shape as DeviatedLineItemsCount - react-query dedupes this to one request.
  const { data, isFetching, isLoading } = useQueryFetch(
    [userId, 'deviatedLineItemsCount', filters, cacheSeed],
    // react-query aborts the signal once this key is superseded (filter change) or unmounted
    ({ signal }) => getDeviatedLineItemsCount(userId, filters, signal),
    { cacheTime: 30 * 1000 }
  );

  if (isFetching || isLoading) {
    return (
      <div className="deviatedLineItemsStats">
        <LoadingSkeleton height={32} width={70} />
        <LoadingSkeleton height={32} width={70} />
      </div>
    );
  }

  const expectedForecastGapLabel = formatMessage({
    defaultMessage: 'Expected vs Forecast',
    id: 'summaryBar.deviatedLineItemsStats.expectedForecastGap'
  });
  const averageDeviationLabel = formatMessage({
    defaultMessage: 'Avg. Deviation',
    id: 'summaryBar.deviatedLineItemsStats.averageDeviation'
  });

  return (
    <div className="deviatedLineItemsStats">
      <div className="deviatedLineItemsStats__metric">
        <div className="deviatedLineItemsStats__metricTop">
          <i aria-hidden className="deviatedLineItemsStats__icon fa-solid fa-sack-dollar" />
          <span className="deviatedLineItemsStats__value">
            {/* Always USD: surviving line items can span accounts in different currencies, so the
                aggregate is computed from the USD fields rather than any single account's currency. */}
            {Formatters.currency(data?.expectedForecastGap ?? 0, { currencyPosition: 'left', currencySymbol: '$' })}
          </span>
          <IconTooltip
            effect="solid"
            event="mouseenter"
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500) !important"
            place="bottom"
          >
            {formatMessage({
              defaultMessage:
                "Expected minus forecast for the current month, summed across line items. Line items forecast to meet or beat their expected delivery count as 0. Paused line items aren't considered.",
              id: 'summaryBar.deviatedLineItemsStats.expectedForecastGapTooltip'
            })}
          </IconTooltip>
        </div>
        <span className="deviatedLineItemsStats__label">{expectedForecastGapLabel}</span>
      </div>
      <div className="deviatedLineItemsStats__metric">
        <div className="deviatedLineItemsStats__metricTop">
          <i aria-hidden className="deviatedLineItemsStats__icon fa-solid fa-arrows-split-up-and-left" />
          <span className="deviatedLineItemsStats__value">{renderPercentage(data?.deviationPercentage)}</span>
          <IconTooltip
            effect="solid"
            event="mouseenter"
            icon="fa-regular fa-circle-info"
            iconColor="var(--color-sonata-primary-500) !important"
            place="bottom"
          >
            {formatMessage({
              defaultMessage:
                "Percentage deviation between the total forecast and the total expected delivery of the current month, both summed across all line items. Paused line items aren't considered.",
              id: 'summaryBar.deviatedLineItemsStats.averageDeviationTooltip'
            })}
          </IconTooltip>
        </div>
        <span className="deviatedLineItemsStats__label">{averageDeviationLabel}</span>
      </div>
    </div>
  );
};

DeviatedLineItemsStats.displayName = 'DeviatedLineItemsStats';

export default DeviatedLineItemsStats;
